<template>
    <div>
        <!-- 1. Composition du Green-score -->
        <div class="blk">
            <p class="eyebrow">Composition du Green-score</p>
            <p v-if="!canSeeDetails" class="hint">Réservé aux <a target="_blank" :href="urlLinkAboPro">{{ textLinkAboPro }}</a></p>
            <template v-else-if="isDef(dg(result, 'singlescore.ecoscore'))">
                <div class="scale" role="img" :aria-label="'Échelle Green-score, note obtenue ' + String(dg(result, 'singlescore.ecoscore_lettre', '')).toUpperCase()">
                    <figure v-for="l in ['a+', 'a', 'b', 'c', 'd', 'e', 'f']" :key="l" :class="{on: l === dg(result, 'singlescore.ecoscore_lettre')}">
                        <img :src="gsPicto(l)" alt="">
                        <figcaption>{{ l.toUpperCase() }}</figcaption>
                    </figure>
                </div>
                <div class="kv"><span class="k">Note environnementale</span><span class="v tnum">{{ fmt(dg(result, 'singlescore.singlescore100'), 1) }} / 100</span></div>
                <div class="kv" v-for="t in bonusTerms" :key="t.key" v-if="isDef(dg(result, 'singlescore.' + t.key)) && round(dg(result, 'singlescore.' + t.key), 1) !== 0">
                    <span class="k">{{ t.label }}</span><span class="v tnum">{{ signed(dg(result, 'singlescore.' + t.key)) }}</span>
                </div>
                <div class="kv total"><span class="k">Green-score</span><span class="v tnum">{{ fmt(dg(result, 'singlescore.ecoscore'), 1) }} / 100</span></div>
            </template>
            <p v-else class="hint">Disponible après le premier calcul.</p>
        </div>

        <!-- 2. Coût environnemental par étape -->
        <div class="blk" v-if="canSeeDetails">
            <p class="eyebrow">Coût environnemental par étape</p>
            <!-- Badge officiel redessiné en vectoriel (repères du kit : contour arrondi, bouclier pixellisé, pastille grise /100 g) -->
            <div class="cebw" v-if="ceBadge">
                <svg class="cebadge" viewBox="40 38 574 272" role="img" :aria-label="ceBadge.aria">
                    <defs><clipPath id="ce-shield"><circle cx="180" cy="173.5" r="126.5"/></clipPath></defs>
                    <path d="M180 45H595a12 12 0 0 1 12 12V290a12 12 0 0 1-12 12H180a128.5 128.5 0 0 1 0-257Z" fill="#fff" stroke="#000" stroke-width="4"/>
                    <g clip-path="url(#ce-shield)" fill="#000">
                        <path d="M40 150H72V134H88V150H104V198H120V246H136V310H40Z"/>
                        <rect v-for="(r, i) in cePixels" :key="i" :x="r[0]" :y="r[1]" width="16" height="16"/>
                    </g>
                    <g fill="#fff"><rect x="72" y="182" width="16" height="16"/><rect x="88" y="214" width="16" height="16"/><rect x="104" y="262" width="16" height="16"/></g>
                    <text x="580" y="84" text-anchor="end" font-size="25" fill="#000">COÛT <tspan font-weight="700">ENVIRONNEMENTAL</tspan></text>
                    <text x="583" y="183" text-anchor="end" :font-size="ceBadge.fn" font-weight="700" letter-spacing="-3" fill="#000">{{ ceBadge.nb }}</text>
                    <text x="580" y="224" text-anchor="end" font-size="32" font-weight="700" fill="#000">points d’impact</text>
                    <rect :x="586 - ceBadge.pw" y="240" :width="ceBadge.pw" height="50" rx="9" fill="#555"/>
                    <text :x="586 - ceBadge.pw / 2" y="275" text-anchor="middle" font-size="30" fill="#fff">{{ ceBadge.pl }}</text>
                </svg>
            </div>
            <div v-for="s in stages" :key="s.key" class="stage" :class="{off: !s.filled}">
                <div class="n"><span>{{ s.label }}</span><span class="tnum">{{ s.filled ? fmt(s.percent) + ' %' : 'non renseigné' }}</span></div>
                <div class="bar"><div class="bar-fill" :class="{'bar-fill--muted': !s.filled}" :style="{width: (s.filled ? s.percent : 0) + '%'}"></div></div>
            </div>
        </div>

        <!-- 3. Provenance -->
        <div class="blk" v-if="isDef(dg(result, 'calcul.nbFranceForcent'))">
            <p class="eyebrow">Provenance</p>
            <div class="segbar" role="img" :aria-label="'France ' + fmt(pctFrance) + ' %, importé ' + fmt(100 - pctFrance) + ' %'">
                <span class="seg-a" :style="{width: pctFrance + '%'}"></span><span class="seg-import" :style="{width: (100 - pctFrance) + '%'}"></span>
            </div>
            <div class="legend">
                <span v-if="pctFrance > 0"><i class="dot dot-a"></i>France {{ fmt(pctFrance) }} %</span>
                <span v-if="pctFrance < 100"><i class="dot dot-import"></i>Importé {{ fmt(100 - pctFrance) }} %</span>
            </div>
        </div>

        <!-- 4. Modes de production -->
        <div class="blk" v-if="isDef(dg(result, 'calcul.nbLabelForcent'))">
            <p class="eyebrow">Modes de production</p>
            <div class="segbar" role="img" :aria-label="'Labellisé ' + fmt(pctLabel) + ' %, sans label ' + fmt(100 - pctLabel) + ' %'">
                <span class="seg-label" :style="{width: pctLabel + '%'}"></span><span class="seg-b" :style="{width: (100 - pctLabel) + '%'}"></span>
            </div>
            <div class="legend">
                <span v-if="pctLabel > 0"><i class="dot dot-label"></i>Labellisé {{ fmt(pctLabel) }} %</span>
                <span v-if="pctLabel < 100"><i class="dot dot-b"></i>Sans label {{ fmt(100 - pctLabel) }} %</span>
                <span v-if="isDef(dg(result, 'calcul.nbBioForcent'))">dont bio {{ fmt(dg(result, 'calcul.nbBioForcent')) }} %</span>
            </div>
        </div>

        <!-- 5. Score social -->
        <div class="blk">
            <p class="eyebrow">Score social</p>
            <p v-if="!canSeeDetails" class="hint">Réservé aux <a target="_blank" :href="urlLinkAboPro">{{ textLinkAboPro }}</a></p>
            <template v-else-if="isDef(dg(result, 'calcul.pointcfinalLETTRE'))">
                <div class="social">
                    <span class="score-letter score-letter--sm" :class="'l-' + dg(result, 'calcul.pointcfinalLETTRE')">{{ String(dg(result, 'calcul.pointcfinalLETTRE')).toUpperCase() }}</span>
                    <div><div class="note tnum">{{ fmt(dg(result, 'calcul.pointcfinal'), 1) }} sur 5</div><div class="hint">Circuits courts, origine France et labels</div></div>
                </div>
                <!-- Trois composantes notées sur 5 (pointcN est déjà pondéré : on remonte à la note brute), cinq glyphes comme la maquette -->
                <div class="socg">
                    <div v-for="c in socialParts" :key="c.label">
                        <span>{{ c.label }}</span>
                        <i :aria-label="c.note + ' sur 5'"><b v-for="k in 5" :key="k" :class="{off: k > c.note}">▊</b></i>
                        <em>pondéré {{ c.weight }} %</em>
                    </div>
                </div>
            </template>
            <p v-else class="hint">Disponible après le premier calcul.</p>
        </div>

        <!-- 6. Repères -->
        <div class="blk">
            <p class="eyebrow">Repères de la recette</p>
            <div class="kv" v-for="r in reperes" :key="r.label" v-if="isDef(r.value)"><span class="k">{{ r.label }}</span><span class="v tnum">{{ r.value }}</span></div>
        </div>
    </div>
</template>

<script>
import format from './format';

export default {
    name: 'ImpactDetailCard',
    mixins: [format],
    props: {
        result: {type: Object, default: null},
        userRole: {type: String, default: ''},
        canSeeDetails: {type: Boolean, default: false},
        urlLinkAboPro: {type: String, default: ''},
        textLinkAboPro: {type: String, default: 'abonnements pro'},
    },
    data() {
        return {
            bonusTerms: [
                {key: 'impactOrigine', label: 'Bonus origine'},
                {key: 'impactLabels', label: 'Bonus labels'},
                {key: 'impactSaison', label: 'Bonus saisonnalité'},
                {key: 'impactEspeceMenace', label: 'Malus espèce menacée'},
            ],
        };
    },
    computed: {
        // Valeurs du badge coût environnemental ; la taille de police suit le nombre de caractères (zones de largeur fixe)
        // Pixels dispersés du bouclier (coordonnées du viewBox, carrés de 16)
        cePixels() {
            return [[88, 118], [104, 134], [120, 102], [136, 150], [120, 166], [152, 182], [136, 214], [152, 86], [104, 86], [152, 246]];
        },
        ceBadge() {
            const pts = this.dg(this.result, 'singlescore.cout_environnemental');
            const p100 = this.dg(this.result, 'singlescore.cout_environnemental100');
            if (!this.isDef(pts) || !this.isDef(p100)) return null;
            const nb = this.fmt(pts);
            const pl = this.fmt(p100) + ' pts/100g';
            return {
                nb, pl,
                // Le chiffre tient dans la zone texte (~360 unités) ; la pastille s'élargit avec son libellé
                fn: Math.min(112, 360 / (0.62 * nb.length)).toFixed(1),
                pw: Math.max(232, Math.round(pl.length * 16.5 + 34)),
                aria: 'Coût environnemental ' + nb + ' points d\'impact, ' + this.fmt(p100) + ' points pour 100 g',
            };
        },
        socialParts() {
            const note = (key, w) => Math.max(0, Math.min(5, Math.round((parseFloat(this.dg(this.result, 'calcul.' + key, 0)) || 0) / w)));
            return [
                {label: 'Circuits courts', note: note('pointc1', 0.2), weight: 20},
                {label: 'Origine France', note: note('pointc2', 0.4), weight: 40},
                {label: 'Produits labellisés', note: note('pointc3', 0.4), weight: 40},
            ];
        },
        pctFrance() { return Math.min(100, Math.max(0, this.round(this.dg(this.result, 'calcul.nbFranceForcent', 0)))); },
        pctLabel() { return Math.min(100, Math.max(0, this.round(this.dg(this.result, 'calcul.nbLabelForcent', 0)))); },
        stages() {
            const s = (k) => parseFloat(this.dg(this.result, 'singlescore.' + k, 0)) || 0;
            const totalPoids = parseFloat(this.dg(this.result, 'totalPoids', 0)) || 0;
            const totalMpt = s('singlescoreMpt100') * totalPoids / 100;
            const transformation = s('impactsTransformationMpt');
            const packaging = s('impactsPackageMpt') + s('impactsEcobalysePackagingMpt');
            const delivery = s('impactsDeliveryMpt');
            const preparation = s('impactsPreparationMpt');
            // Part des ingrédients = total de la recette − étapes aval (singlescoreIngredients est un tableau de malus par ligne, pas un total)
            const ingredients = Math.max(0, totalMpt - transformation - packaging - delivery - preparation);
            const raw = [
                {key: 'ing', label: 'Ingrédients', value: ingredients, filled: totalPoids > 0},
                {key: 'tr', label: 'Transformation', value: transformation, filled: this.isDef(this.dg(this.result, 'singlescore.impactsTransformationMpt'))},
                {key: 'emb', label: 'Emballage', value: packaging, filled: packaging > 0},
                {key: 'dis', label: 'Distribution', value: delivery, filled: delivery > 0},
                {key: 'co', label: 'Consommation', value: preparation, filled: this.isDef(this.dg(this.result, 'singlescore.impactsPreparationMpt')) && preparation > 0},
            ];
            const total = raw.reduce((a, x) => a + (x.filled ? x.value : 0), 0);
            return raw.map((x) => ({...x, percent: total > 0 && x.filled ? this.round(x.value / total * 100) : 0}));
        },
        reperes() {
            const r = this.result;
            const pct = (k) => this.isDef(this.dg(r, 'calcul.' + k)) ? this.fmt(this.dg(r, 'calcul.' + k)) + ' %' : undefined;
            const fl = this.dg(r, 'totalPoidsFruitLegumeHerbes'), tot = this.dg(r, 'totalPoidsRaw');
            return [
                {label: 'Poids par portion', value: this.isDef(this.dg(r, 'totalPoidsPortion')) ? this.fmt(this.dg(r, 'totalPoidsPortion')) + ' g' : undefined},
                {label: 'Fruits et légumes', value: this.isDef(fl) && tot ? this.fmt(fl / tot * 100) + ' %' : undefined},
                {label: 'Produits de saison', value: pct('nbSaisonForcent')},
                {label: 'Part biologique', value: pct('nbBioForcent')},
                {label: 'Produits locaux', value: pct('nbLocalForcent')},
                {label: 'Circuits courts', value: pct('nbCircuitCourtForcent')},
                {label: 'Commerce équitable', value: pct('nbCommerceEquitableForcent')},
                {label: 'Label Rouge sur viandes et poissons', value: pct('nbLabelRougeForcent')},
                {label: 'Pêche durable (MSC)', value: pct('nbMscForcent')},
                {label: 'Empreinte pour 100 g', value: this.isDef(this.dg(r, 'gCO2e100')) ? this.fmt(this.dg(r, 'gCO2e100')) + ' g CO2e' : undefined},
                {label: 'Dont étapes aval', value: this.canSeeDetails && this.isDef(this.dg(r, 'singlescore.singlescorePackageForcent')) ? this.fmt((parseFloat(this.dg(r, 'singlescore.singlescorePackageForcent', 0)) || 0) + (parseFloat(this.dg(r, 'singlescore.singlescoreDeliveryForcent', 0)) || 0)) + ' % du single score' : undefined},
                {label: 'Coût environnemental / portion', value: this.canSeeDetails && this.isDef(this.dg(r, 'singlescore.cout_environnemental')) ? this.fmt(this.dg(r, 'singlescore.cout_environnemental')) + ' pts' : undefined},
            ];
        },
    },
    methods: {
        signed(v) {
            const n = this.round(v, 1);
            return (n > 0 ? '+' : '') + this.fmt(n, 1);
        },
    },
};
</script>
